import { useCallback, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

interface UsePersistentObjectOptions<T> {
  paramName?: string;
  storageKey?: string;
  defaultValue?: T;
}

export default function usePersistentObject<T extends object>(
  options: UsePersistentObjectOptions<T>,
) {
  const { paramName, storageKey, defaultValue } = options;

  const { search, pathname } = useLocation();

  const history = useHistory();

  const [state, setState] = useState<T>(() => {
    if (paramName) {
      const value = getParamValue(search, paramName);
      if (value) {
        return stringToObject(value) as T;
      }
    }
    if (storageKey) {
      const value = getStorageValue(storageKey);
      if (value) {
        return stringToObject(value) as T;
      }
    }
    return defaultValue as T;
  });

  const handleSetState = useCallback(
    (value: T | ((prev: T) => T)) => {
      const nextState = typeof value === "function" ? value(state) : value;
      setState(nextState);

      const urlSearchParams = new URLSearchParams(search);
      if (paramName) {
        if (!nextState) {
          urlSearchParams.delete(paramName);
        } else {
          const formatted = objectToString(nextState);
          if (formatted) {
            urlSearchParams.set(paramName, formatted);
            const params = urlSearchParams.toString();
            const url = `${pathname}?${params}`;
            history.replace(url);
          }
        }
      }
      if (storageKey) {
        if (!nextState) {
          localStorage.removeItem(storageKey);
        } else {
          const formatted = objectToString(nextState);
          if (formatted) {
            localStorage.setItem(storageKey, formatted);
          }
        }
      }
    },
    [search, paramName, storageKey, pathname, history, state],
  );

  return [state, handleSetState] as const;
}

function getStorageValue(key: string) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return undefined;
  }
}

function getParamValue(search: string, name: string) {
  try {
    const urlSearchParams = new URLSearchParams(search);
    return urlSearchParams.get(name);
  } catch (error) {
    return undefined;
  }
}

function objectToString(obj: object): string {
  const stringValue = JSON.stringify(obj, (key, value) => {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  });
  return encodeToBase64(stringValue);
}

function stringToObject<T>(str: string): T {
  const decoded = decodeFromBase64(str);
  return JSON.parse(decoded, (key, value) => {
    if (typeof value === "string") {
      // Check if the string value represents a Date
      const dateMatch =
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})Z$/.exec(
          value,
        );
      if (dateMatch) {
        // Convert string representation back to Date object
        return new Date(
          Date.UTC(
            parseInt(dateMatch[1], 10),
            parseInt(dateMatch[2], 10) - 1,
            parseInt(dateMatch[3], 10),
            parseInt(dateMatch[4], 10),
            parseInt(dateMatch[5], 10),
            parseInt(dateMatch[6], 10),
            parseInt(dateMatch[7], 10),
          ),
        );
      }
    }
    return value;
  });
}

function encodeToBase64(str: string) {
  return window.btoa(encodeURIComponent(str));
}

function decodeFromBase64(str: string) {
  return decodeURIComponent(window.atob(str));
}
